

import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, useTheme } from '@mui/material';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import SecondaryLoader from '../loaders/SecondaryLoader';

export default function HeritageBody() {
  const theme = useTheme();
  const navigate = useNavigate();

  const patternUrl = "https://brokepiggytoken.xyz/chatgpt-image-jul-14-2025-12_53_49-pm/";
  const secondImgUrl = "https://brokepiggytoken.xyz/wp-content/uploads/2025/07/filteredoil-reserve-1080-x-1080-px.png";
  const thirdImgUrl = "https://brokepiggytoken.xyz/wp-content/uploads/2025/07/olive-growing-reserve.jpg";
  const headerDesign =
    'https://brokepiggytoken.xyz/wp-content/uploads/2025/07/from-marrakech-with-heritage-8.png';
  const headerDesignWhite = 'https://brokepiggytoken.xyz/wp-content/uploads/2025/07/from-marrakech-with-heritage-7-scaled.png';
  const accent = '#b0a990';

  // Loader state
  const [loading, setLoading] = useState(false);

  // Intersection animation
  const controls = useAnimation();
  const { ref, inView } = useInView({ once: true, threshold: 0.3 });
  useEffect(() => {
    if (inView) controls.start('visible');
  }, [controls, inView]);

  const textVariants = {
    hidden: { x: -50, opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { delay: 0.3, duration: 0.6 } }
  };

  const handleClick = () => {
    setLoading(true);
    setTimeout(() => {
      navigate('/our-heritage');
    }, 2000); // show loader for at least 2 seconds
  };

  return (
    <>
      <AnimatePresence>
        {loading && <SecondaryLoader key="secondary-loader" />}
      </AnimatePresence>

      <Box
        id="about"
        ref={ref}
        sx={{
          position: 'relative',
          width: '100%',
          bgcolor: 'common.white',
          color: 'common.black',
          pt: { xs: 4, md: 4 },  // only top padding
          overflow: 'hidden',
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', md: 'row' },
            alignItems: 'center',
            gap: { xs: 2, md: 4 },
          }}
        >
         
          <Box
  sx={{
    flex: 1,
    width: '100%',
    height: { xs: 150, md: 350 },
    backgroundImage: `url(${patternUrl})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    borderRadius: 3,     // rounded corners
    overflow: 'hidden',  // clip the bg within those corners
  }}
/>

           {/* TEXT */}
          <motion.div
            variants={textVariants}
            initial="hidden"
            animate={controls}
            style={{ flex: 1 }}
          >
            <Box
              component="img"
              src={headerDesign}
              alt="From Marrakech with Heritage"
              sx={{
                width: { xs: 120, md: 160 },
                mb: 4,
                display: 'block',
                ml: { xs: '-16px', md: '-24px' },
              }}
            />

            {/* Accent bar */}
            <Box
              sx={{
                width: 60,
                height: 4,
                bgcolor: 'secondary.main',
                mb: 2,
                borderRadius: 2,
              }}
            />

            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.6,
                fontSize: { xs: '0.95rem', md: '1rem' },
                mb: 3,
              }}
            >
 Resting at the crossroads of ancient caravan routes, Marrakech has inspired poets,
        artists, and travelers for centuries. Beyond the bustling souks and towering minarets
        lies the untold story of our olive groves — a narrative woven with the earth, the sun,
        and generations of devotion.
        
                    </Typography>

                    <Typography
              variant="body1"
              sx={{
                lineHeight: 1.6,
                fontSize: { xs: '0.95rem', md: '1rem' },
                mb: 3,
              }}
            >
 Our family’s journey began on the ochre plains just outside the Medina’s gates, where
        age-old olive trees stand sentinel over red-soil terraces. Passed down from grandparents
        to parents to children, these groves are living heritage. Every spring, we watch new
        shoots emerge, each leaf a promise of the golden elixir to come.
                    </Typography>


            <Box
              component="img"
              src={headerDesign}
              alt="From Marrakech with Heritage"
              sx={{
                width: { xs: 120, md: 160 },
                mt: 4,
                mb: 4,
                display: 'block',
                ml: 'auto',
                mr: { xs: '-16px', md: '-24px' },
              }}
            />
          </motion.div>

        </Container>

        <Box
        ref={ref}
        sx={{
          position: 'relative',
          width: '100%',
          bgcolor: 'common.black',
          color: 'common.white',
          py: { xs: 4, md: 4 },
          overflow: 'hidden',
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', md: 'row' },
            alignItems: 'center',
            gap: { xs: 2, md: 4 },
          }}
        >
          {/* TEXT */}
          <motion.div
            variants={textVariants}
            initial="hidden"
            animate={controls}
            style={{ flex: 1 }}
          >
            <Box
              component="img"
              src={headerDesignWhite}
              alt="From Marrakech with Heritage"
              sx={{
                width: { xs: 120, md: 160 },
                mb: 4,
                display: 'block',
                ml: { xs: '-16px', md: '-24px' },
              }}
            />

            {/* Accent bar */}
            <Box
              sx={{
                width: 60,
                height: 4,
                bgcolor: 'secondary.main',
                mb: 2,
                borderRadius: 2,
              }}
            />


              <Typography
              variant="body1"
              sx={{
                color: '#FFFFFF',
                lineHeight: 1.6,
                fontSize: { xs: '0.95rem', md: '1rem' },
                mb: 3,
              }}
            >
                     Harvest in November is a sacred ritual. At dawn, under skies brushed with rose and
        amber, our hands glide through fragrant branches, coaxing plump olives into woven baskets.
        The air hums with the promise of the season — deep green fruits that carry the heartbeat
        of Marrakech in every drop.
      </Typography>


<Typography
              variant="body1"
              sx={{
                color: '#FFFFFF',
                lineHeight: 1.6,
                fontSize: { xs: '0.95rem', md: '1rem' },
                mb: 3,
              }}
            >
      In our mill, tradition meets precision. Cold-pressing below 25 °C and gentle decantation
        capture notes of basil, sun-warmed herbs, and a subtle hint of ripe almond. The result is
        an extra-virgin olive oil that speaks of place and passion.
      </Typography>

<Typography
              variant="body1"
              sx={{
                lineHeight: 1.6,
                color: '#FFFFFF',
                fontSize: { xs: '0.95rem', md: '1rem' },
                mb: 3,
              }}
            >

 When you drizzle our olive oil over crisp bread or vibrant salads, know that you’re
        tasting more than flavor. You are savoring a legacy: sunlit mornings in Marrakech,
        patient hands, and an unbroken bond between people and tree. This is heritage you can
        pour — an invitation to carry our story into your kitchen and onto your table.
      </Typography>


            <Box
              component="img"
              src={headerDesignWhite}
              alt="From Marrakech with Heritage"
              sx={{
                width: { xs: 120, md: 160 },
                mt: 4,
                display: 'block',
                ml: 'auto',
                mr: { xs: '-16px', md: '-24px' },
              }}
            />
          </motion.div>

          {/* DESIGN SIDE */}
<Box
  sx={{
    flex: 1,
    width: '100%',
    height: { xs: 150, md: 350 },
    backgroundImage: `url(${thirdImgUrl})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    borderRadius: 3,     // rounded corners
    overflow: 'hidden',  // clip the bg within those corners
  }}
/>

        </Container>
      </Box>

      
      </Box>
    </>
  );
}
